/**
 * The month grid the design shows twice: as a bottom sheet under 768px and
 * as an inline card beside the game list at `lg`. One grid, two frames.
 * Days with games carry a primary dot, a pointer cursor and a hover fill (ISSUE-976).
 */
import { Button, cn, FOCUS_RING } from "@sportsmet/ui";
import { type ReactNode } from "react";

import { longDayLabel, monthLabel, shiftMonth } from "./calendarDay";
import {
  JUMP_TO_TODAY_LABEL,
  NEXT_MONTH_LABEL,
  PREVIOUS_MONTH_LABEL,
} from "./leagueSelectorContract";
import { buildMonthGrid, WEEKDAY_HEADINGS } from "./monthGrid";

export type typeMonthCalendarProps = {
  /** Any day inside the month being drawn. Owned by the pane above, in
   *  plain component state: which month is scrolled to is not addressable
   *  state -- `?date=` is. Opening the calendar always starts on the
   *  selected day's month. */
  monthAnchorDay: string;
  onMonthAnchorChange: (day: string) => void;
  /** Only days with games, for the visible month. */
  daysWithGames: readonly string[];
  todayDay: string;
  selectedDay: string;
  /** The last day of a selected RANGE, inclusive, "YYYY-MM-DD". Set only on
   *  a week-based league, where the selection is a whole week: every day from
   *  `selectedDay` through this one is marked. Left out on a day-based league,
   *  which marks `selectedDay` alone. ISSUE-958. */
  selectedLastDay?: string | undefined;
  onSelectDay: (day: string) => void;
};

const MonthCalendarBody = (props: typeMonthCalendarProps): ReactNode => {
  const cells = buildMonthGrid({
    monthAnchorDay: props.monthAnchorDay,
    daysWithGames: props.daysWithGames,
    todayDay: props.todayDay,
    selectedDay: props.selectedDay,
    selectedLastDay: props.selectedLastDay,
  });
  const isRangeSelection = props.selectedLastDay !== undefined;

  return (
    <div data-testid="month-calendar">
      <div className="flex items-center justify-between">
        <button
          type="button"
          className={cn("p-2", FOCUS_RING)}
          data-testid="month-calendar-prev"
          aria-label={PREVIOUS_MONTH_LABEL}
          onClick={() => {
            props.onMonthAnchorChange(
              shiftMonth({ day: props.monthAnchorDay, deltaMonths: -1 }),
            );
          }}
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="m10 3-4 5 4 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <span className="text-body-strong">
          {monthLabel(props.monthAnchorDay)}
        </span>
        <button
          type="button"
          className={cn("p-2", FOCUS_RING)}
          data-testid="month-calendar-next"
          aria-label={NEXT_MONTH_LABEL}
          onClick={() => {
            props.onMonthAnchorChange(
              shiftMonth({ day: props.monthAnchorDay, deltaMonths: 1 }),
            );
          }}
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="m6 3 4 5-4 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      <div className="mt-2 grid grid-cols-7 gap-1 text-center">
        {WEEKDAY_HEADINGS.map((heading) => (
          <span
            key={heading}
            className="text-small font-semibold text-ink-tertiary"
          >
            {heading}
          </span>
        ))}
        {cells.map((cell, index) => {
          if (cell.day === null) {
            return (
              <span
                // Blank padding cells carry no day and are never reordered
                // relative to one another, so the index is a stable key here.
                key={`blank-${index}`}
                className="min-h-13"
                data-testid="month-calendar-inert"
              />
            );
          }

          // Narrowed to a local so the click handler below closes over a
          // `string`, not the `string | null` on `cell.day` -- TypeScript
          // does not carry a property narrowing across a closure boundary.
          const day = cell.day;

          if (!cell.hasGames) {
            return (
              <span
                key={day}
                className={cn(
                  "grid min-h-13 place-items-center text-body-strong tabular-nums",
                  cell.isSelected
                    ? "bg-primary-solid text-on-primary"
                    : "text-ink-tertiary",
                  cell.isToday && !cell.isSelected
                    ? "border border-primary"
                    : undefined,
                  isRangeSelection && cell.isToday && cell.isSelected
                    ? "outline-2 outline-on-primary -outline-offset-4"
                    : undefined,
                )}
                data-testid="month-calendar-inert"
                data-day={day}
                aria-current={cell.isSelected ? "date" : undefined}
              >
                {cell.dayOfMonth}
              </span>
            );
          }

          return (
            <button
              key={day}
              type="button"
              className={cn(
                "flex min-h-13 cursor-pointer flex-col items-center justify-center gap-1 rounded-sm text-body-strong tabular-nums",
                cell.isSelected
                  ? "bg-primary-solid text-on-primary hover:bg-primary-solid-hover"
                  : "hover:bg-surface-sunken",
                cell.isToday && !cell.isSelected
                  ? "border border-primary"
                  : undefined,
                isRangeSelection && cell.isToday && cell.isSelected
                  ? "outline-2 outline-on-primary -outline-offset-4"
                  : undefined,
                FOCUS_RING,
              )}
              data-testid="month-calendar-day"
              data-day={day}
              aria-current={cell.isSelected ? "date" : undefined}
              aria-label={longDayLabel(day)}
              onClick={() => {
                props.onSelectDay(day);
              }}
            >
              {cell.dayOfMonth}
              <span
                aria-hidden="true"
                data-testid="month-calendar-game-dot"
                className={cn(
                  "size-2 rounded-full",
                  cell.isSelected ? "bg-on-primary" : "bg-primary",
                )}
              />
              {cell.gameCount !== null ? (
                <span className="text-caption">{cell.gameCount}</span>
              ) : null}
            </button>
          );
        })}
      </div>
      <Button
        variant="link"
        data-testid="month-calendar-today"
        onClick={() => {
          props.onMonthAnchorChange(props.todayDay);
        }}
      >
        {JUMP_TO_TODAY_LABEL}
      </Button>
    </div>
  );
};

export const MonthCalendarGrid = (props: typeMonthCalendarProps): ReactNode => (
  <MonthCalendarBody {...props} />
);

export const MonthCalendarCard = (props: typeMonthCalendarProps): ReactNode => (
  <div className="rounded-md border border-hairline bg-surface p-4">
    <MonthCalendarBody {...props} />
  </div>
);
